import { z } from 'zod';
import { IsoDateTime, PageMeta, PageRequest, MutationReason } from './_common.js';
export const IapReceiptStatus = z.enum(['pending', 'completed', 'refunded', 'chargebacked', 'failed']);
export const IapReceipt = z.object({
    id: z.number().int(),
    user_id: z.string(),
    sku: z.string(),
    amount_cents: z.number().int(),
    currency: z.string().length(3),
    amount_usd_cents: z.number().int(),
    xsolla_receipt_id: z.string(),
    status: IapReceiptStatus,
    country_code: z.string().nullable(),
    region_subdivision: z.string().nullable(),
    ledger_entry_id: z.number().int().nullable(),
    created_at: IsoDateTime,
    completed_at: IsoDateTime.nullable(),
    refunded_at: IsoDateTime.nullable(),
});
export const IapReceiptListRequest = PageRequest.extend({
    user_id: z.string().optional(),
    sku: z.string().optional(),
    status: IapReceiptStatus.optional(),
    country_code: z.string().length(2).optional(),
    ts_after: IsoDateTime.optional(),
    ts_before: IsoDateTime.optional(),
});
export const IapReceiptListResponse = z.object({
    items: z.array(IapReceipt),
    page: PageMeta,
    sum_amount_usd_cents: z.number().int(),
});
export const ReconcileXsollaRequest = z.object({
    date_utc: z.string(),
    reason: MutationReason,
});
export const ReconcileXsollaResponse = z.object({
    date_utc: z.string(),
    xsolla_total_usd_cents: z.number().int(),
    ledger_iap_total_usd_cents: z.number().int(),
    diff_usd_cents: z.number().int(),
    receipt_count_xsolla: z.number().int(),
    receipt_count_ledger: z.number().int(),
    missing_in_ledger: z.array(z.string()),
    missing_in_xsolla: z.array(z.string()),
    status: z.enum(['matched', 'drift']),
});
export const RefundRequestRequest = z.object({
    receipt_id: z.number().int(),
    reason: MutationReason,
    clawback_coins: z.boolean().default(true),
});
export const RefundRequestResponse = z.object({
    receipt_id: z.number().int(),
    xsolla_refund_id: z.string(),
    refunded_at: IsoDateTime,
    coins_clawed_back: z.number().int(),
    ledger_clawback_entry_id: z.number().int().nullable(),
});
export const ChargebackRecordRequest = z.object({
    receipt_id: z.number().int(),
    xsolla_chargeback_id: z.string(),
    reason: MutationReason,
});
export const ChargebackRecordResponse = z.object({
    receipt_id: z.number().int(),
    recorded_at: IsoDateTime,
    user_flagged: z.boolean(),
    coins_clawed_back: z.number().int(),
});
export const RevenueReportRequest = z.object({
    group_by: z.enum(['day', 'week', 'month', 'sku', 'country', 'cohort']).default('day'),
    ts_after: IsoDateTime,
    ts_before: IsoDateTime,
    sku: z.string().optional(),
    country_code: z.string().length(2).optional(),
    cohort_id: z.string().optional(),
});
export const RevenueReportRow = z.object({
    bucket: z.string(),
    receipts: z.number().int(),
    revenue_usd_cents: z.number().int(),
    unique_payers: z.number().int(),
    refunds_usd_cents: z.number().int(),
    chargebacks_usd_cents: z.number().int(),
    net_usd_cents: z.number().int(),
});
export const RevenueReportResponse = z.object({
    group_by: z.string(),
    rows: z.array(RevenueReportRow),
    totals: RevenueReportRow,
});
export const WhaleListRequest = PageRequest.extend({
    lifetime_iap_min_cents: z.number().int().default(50000),
    ts_active_after: IsoDateTime.optional(),
});
export const WhaleEntry = z.object({
    user_id: z.string(),
    username: z.string(),
    display_name: z.string(),
    current_tier: z.string(),
    lifetime_iap_cents: z.number().int(),
    iap_30d_cents: z.number().int(),
    iap_7d_cents: z.number().int(),
    last_purchase_at: IsoDateTime.nullable(),
    last_seen_at: IsoDateTime.nullable(),
    flag_anomaly: z.boolean(),
});
export const WhaleListResponse = z.object({
    items: z.array(WhaleEntry),
    page: PageMeta,
});
export const EconomyHealthSnapshot = z.object({
    ts: IsoDateTime,
    total_outstanding_balance: z.number().int(),
    daily_issued: z.number().int(),
    daily_consumed: z.number().int(),
    net_inflation_rate_24h_pct: z.number(),
    active_users_24h: z.number().int(),
    median_balance: z.number().int(),
    p95_balance: z.number().int(),
    issuance_breakdown: z.record(z.number().int()),
});
export const EconomyHealthRequest = z.object({
    ts_after: IsoDateTime.optional(),
    ts_before: IsoDateTime.optional(),
});
export const EconomyHealthResponse = z.object({
    current: EconomyHealthSnapshot,
    history: z.array(EconomyHealthSnapshot),
});
export const TaxReportRequest = z.object({
    region: z.enum(['US', 'CA']),
    period_start: IsoDateTime,
    period_end: IsoDateTime,
});
export const TaxReportResponse = z.object({
    region: z.string(),
    period_start: IsoDateTime,
    period_end: IsoDateTime,
    taxable_revenue_usd_cents: z.number().int(),
    tax_collected_usd_cents: z.number().int(),
    by_subdivision: z.array(z.object({
        subdivision: z.string(),
        revenue_usd_cents: z.number().int(),
        tax_usd_cents: z.number().int(),
    })),
    xsolla_remitted: z.boolean(),
});
//# sourceMappingURL=finance.js.map